import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Play, Pause, SkipForward, X, ArrowLeft, Footprints, Target, Flame, Edit3, Check, MapPin, Watch, HelpCircle, Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import { trackUsage, FEATURES } from '../services/usageTracking';

const PHASE = {
  QUICK: 'quick',
  SLOW: 'slow',
  COMPLETE: 'complete'
};

// MET values for calorie calculation
const MET_QUICK_WALKING = 5.0; // Brisk walking ~4 mph
const MET_SLOW_WALKING = 2.5;  // Slow walking ~2 mph

// Storage keys
const DAILY_STEPS_KEY = 'walking_daily_steps';
const EXTERNAL_STEPS_KEY = 'gym_notebook_external_steps';
const TIMER_STATE_KEY = 'walking_timer_state';

// Get today's date string for daily tracking
const getTodayKey = () => new Date().toISOString().split('T')[0];

// Load persisted timer state from localStorage
const loadPersistedTimerState = () => {
  try {
    const saved = localStorage.getItem(TIMER_STATE_KEY);
    if (saved) {
      const state = JSON.parse(saved);
      // Only restore if saved within last 24 hours and not completed
      const savedTime = new Date(state.savedAt).getTime();
      const now = Date.now();
      const hoursSinceSave = (now - savedTime) / (1000 * 60 * 60);
      if (hoursSinceSave < 24 && state.phase !== 'complete') {
        return state;
      }
    }
  } catch (e) {
    console.error('Error loading timer state:', e);
  }
  return null;
};

// Calculate distance between two GPS coordinates (Haversine formula)
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371000; // Earth's radius in meters
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c; // Distance in meters
};

export default function WalkingTimer({ config, onComplete, onBack }) {
  // Load persisted state if available
  const persistedState = loadPersistedTimerState();
  
  const [phase, setPhase] = useState(persistedState?.phase || PHASE.QUICK);
  const [currentQuickSet, setCurrentQuickSet] = useState(persistedState?.currentQuickSet || 1);
  const [currentSlowSet, setCurrentSlowSet] = useState(persistedState?.currentSlowSet || 0);
  const [timeLeft, setTimeLeft] = useState(persistedState?.timeLeft || config.quickWalkingTime);
  const [isPaused, setIsPaused] = useState(persistedState ? true : false); // Start paused if restored
  const [totalElapsedTime, setTotalElapsedTime] = useState(persistedState?.totalElapsedTime || 0);
  const [wasRestored, setWasRestored] = useState(!!persistedState);
  
  // GPS Step counter state
  const [sessionSteps, setSessionSteps] = useState(persistedState?.sessionSteps || 0);
  const [totalDistance, setTotalDistance] = useState(persistedState?.totalDistance || 0);
  const [gpsEnabled, setGpsEnabled] = useState(false);
  const [gpsError, setGpsError] = useState(null);
  const [gpsAccuracy, setGpsAccuracy] = useState(null);
  
  // Wake Lock state - keeps screen on for continuous GPS tracking
  const [wakeLockEnabled, setWakeLockEnabled] = useState(false);
  const [wakeLockSupported, setWakeLockSupported] = useState(false);
  const wakeLockRef = useRef(null);
  
  // Track usage on mount
  useEffect(() => {
    trackUsage(FEATURES.WALKING);
  }, []);
  
  // Show restore notification if timer was restored from persistence
  useEffect(() => {
    if (wasRestored) {
      toast.info('Walking session restored! Tap play to continue.', { duration: 4000 });
      setWasRestored(false);
    }
  }, [wasRestored]);
  
  // Persist timer state to localStorage whenever it changes
  useEffect(() => {
    if (phase === PHASE.COMPLETE) {
      // Clear persisted state when workout is complete
      localStorage.removeItem(TIMER_STATE_KEY);
      return;
    }
    
    const timerState = {
      phase,
      currentQuickSet,
      currentSlowSet,
      timeLeft,
      totalElapsedTime,
      sessionSteps,
      totalDistance,
      savedAt: new Date().toISOString()
    };
    
    localStorage.setItem(TIMER_STATE_KEY, JSON.stringify(timerState));
  }, [phase, currentQuickSet, currentSlowSet, timeLeft, totalElapsedTime, sessionSteps, totalDistance]);
  
  // Check Wake Lock support and auto-enable on mount
  useEffect(() => {
    const checkWakeLockSupport = async () => {
      if ('wakeLock' in navigator) {
        setWakeLockSupported(true);
        // Auto-enable wake lock for better GPS tracking
        try {
          wakeLockRef.current = await navigator.wakeLock.request('screen');
          setWakeLockEnabled(true);
          console.log('[WakeLock] Screen wake lock enabled automatically');
          toast.success('Screen will stay on for GPS tracking', { duration: 3000 });
          
          // Re-acquire wake lock if it's released (e.g., tab switch)
          wakeLockRef.current.addEventListener('release', () => {
            console.log('[WakeLock] Wake lock was released');
            setWakeLockEnabled(false);
          });
        } catch (err) {
          console.log('[WakeLock] Could not acquire wake lock:', err.message);
        }
      } else {
        console.log('[WakeLock] Wake Lock API not supported');
      }
    };
    
    checkWakeLockSupport();
    
    // Cleanup on unmount
    return () => {
      if (wakeLockRef.current) {
        wakeLockRef.current.release();
        wakeLockRef.current = null;
        console.log('[WakeLock] Released on unmount');
      }
    };
  }, []);
  
  // Re-acquire wake lock when page becomes visible again
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && wakeLockSupported && !wakeLockRef.current) {
        try {
          wakeLockRef.current = await navigator.wakeLock.request('screen');
          setWakeLockEnabled(true);
          console.log('[WakeLock] Re-acquired after visibility change');
        } catch (err) {
          console.log('[WakeLock] Could not re-acquire:', err.message);
        }
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [wakeLockSupported]);
  
  // Toggle wake lock manually
  const toggleWakeLock = async () => {
    if (!wakeLockSupported) {
      toast.error('Your browser does not support keeping the screen on');
      return;
    }
    
    if (wakeLockEnabled && wakeLockRef.current) {
      await wakeLockRef.current.release();
      wakeLockRef.current = null;
      setWakeLockEnabled(false);
      toast.info('Screen can now turn off');
    } else {
      try {
        wakeLockRef.current = await navigator.wakeLock.request('screen');
        setWakeLockEnabled(true);
        toast.success('Screen will stay on');
        
        wakeLockRef.current.addEventListener('release', () => {
          setWakeLockEnabled(false);
        });
      } catch (err) {
        toast.error('Could not keep screen on');
      }
    }
  };
  
  // Calorie tracking state
  const [quickWalkingTime, setQuickWalkingTime] = useState(0); // Total quick walking seconds
  const [slowWalkingTime, setSlowWalkingTime] = useState(0);   // Total slow walking seconds
  const [editingCalories, setEditingCalories] = useState(false);
  const [manualCalories, setManualCalories] = useState(null);
  
  // External steps from smartwatch/app (from Nutrition Tracker)
  const [externalSteps, setExternalSteps] = useState(0);
  
  // Step goal from config
  const stepGoalEnabled = config.stepGoalEnabled || false;
  const stepGoal = config.stepGoal || 10000;
  const previousDailySteps = config.todaySteps || 0;
  const bodyWeight = config.bodyWeight || 70; // kg
  const strideLength = config.strideLength || 75; // cm
  
  // Compute total daily steps (walking tracker + external steps from smartwatch)
  const walkingTrackerSteps = previousDailySteps + sessionSteps;
  const totalDailySteps = walkingTrackerSteps + externalSteps;
  
  // Load external steps from localStorage on mount and when date changes
  useEffect(() => {
    const loadExternalSteps = () => {
      const todayKey = getTodayKey();
      try {
        const savedSteps = localStorage.getItem(EXTERNAL_STEPS_KEY);
        if (savedSteps) {
          const stepsData = JSON.parse(savedSteps);
          setExternalSteps(stepsData[todayKey] || 0);
        }
      } catch (e) {
        console.error('Error loading external steps:', e);
      }
    };
    
    loadExternalSteps();
    
    // Listen for storage changes (when external steps are updated in Nutrition Tracker)
    const handleStorageChange = (e) => {
      if (e.key === EXTERNAL_STEPS_KEY) {
        loadExternalSteps();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    // Also poll periodically in case storage event doesn't fire (same tab)
    const interval = setInterval(loadExternalSteps, 5000);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, []);
  
  // Calculate calories burned using multiple methods for better accuracy
  const calculateCalories = () => {
    // Method 1: MET-based calculation using interval times
    const quickHours = quickWalkingTime / 3600;
    const slowHours = slowWalkingTime / 3600;
    const metCalories = Math.round(
      (MET_QUICK_WALKING * bodyWeight * quickHours) +
      (MET_SLOW_WALKING * bodyWeight * slowHours)
    );
    
    // Method 2: Distance-based calculation (0.6 kcal per kg per km)
    const distanceKm = totalDistance / 1000;
    const distanceCalories = Math.round(0.6 * bodyWeight * distanceKm);
    
    // Method 3: Total time based (MET 3.5 for general walking)
    const totalHours = totalElapsedTime / 3600;
    const timeCalories = Math.round(3.5 * bodyWeight * totalHours);
    
    // Use the highest value among the three methods
    const calculatedCal = Math.max(metCalories, distanceCalories, timeCalories, 0);
    
    return calculatedCal;
  };
  
  const calculatedCalories = calculateCalories();
  const displayCalories = manualCalories !== null ? manualCalories : calculatedCalories;
  
  // Clear persisted timer state
  const clearTimerState = () => {
    localStorage.removeItem(TIMER_STATE_KEY);
  };
  
  // Save and Exit - saves partial workout before exiting
  const handleSaveAndExit = () => {
    console.log('[WalkingTimer] Save & Exit clicked:', {
      totalElapsedTime,
      sessionSteps,
      displayCalories,
      totalDistance,
      phase
    });
    
    // Clear persisted state since we're exiting
    clearTimerState();
    
    // Save if there's ANY data worth saving (even 0 steps with some time elapsed)
    if (totalElapsedTime > 0 || sessionSteps > 0 || displayCalories > 0 || totalDistance > 0) {
      const saveData = {
        totalTime: totalElapsedTime,
        quickSets: currentQuickSet - 1,
        slowSets: currentSlowSet,
        steps: sessionSteps,
        totalDailySteps: totalDailySteps,
        calories: displayCalories,
        bodyWeight: bodyWeight,
        distance: totalDistance,
        incomplete: true // Mark as incomplete workout
      };
      console.log('[WalkingTimer] Calling onComplete with:', saveData);
      onComplete(saveData);
    } else {
      console.log('[WalkingTimer] No progress detected, going back without saving');
      toast.info('No walking data to save');
      onBack();
    }
  };
  
  // Handle workout completion
  const handleWorkoutComplete = (data) => {
    clearTimerState();
    onComplete(data);
  };
  
  const audioContextRef = useRef(null);
  const intervalRef = useRef(null);
  const lastPositionRef = useRef(null);
  const watchIdRef = useRef(null);
  const voicesCacheRef = useRef(null);
  const [voicesLoaded, setVoicesLoaded] = useState(false);
  
  // Refs for GPS callback to avoid stale closures
  const isPausedRef = useRef(false); // Initialize to false (not paused)
  const phaseRef = useRef(PHASE.QUICK); // Initialize to QUICK phase
  
  // Keep refs in sync with state
  useEffect(() => {
    isPausedRef.current = isPaused;
    console.log('[GPS Ref] isPaused updated to:', isPaused);
  }, [isPaused]);
  
  useEffect(() => {
    phaseRef.current = phase;
    console.log('[GPS Ref] phase updated to:', phase);
  }, [phase]);

  // Initialize audio context and load voices
  useEffect(() => {
    audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    
    // Load speech synthesis voices and cache them
    const loadVoices = () => {
      const voices = window.speechSynthesis.getVoices();
      if (voices.length > 0) {
        voicesCacheRef.current = voices;
        setVoicesLoaded(true);
        console.log('[Voice] Loaded', voices.length, 'voices');
      }
    };
    
    // Voices may be loaded asynchronously (especially in Chrome)
    loadVoices();
    if (window.speechSynthesis.onvoiceschanged !== undefined) {
      window.speechSynthesis.onvoiceschanged = loadVoices;
    }
    
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      window.speechSynthesis.onvoiceschanged = null;
    };
  }, []);

  // Save daily steps to localStorage whenever sessionSteps changes
  useEffect(() => {
    if (sessionSteps > 0) {
      const todayKey = getTodayKey();
      const dailySteps = JSON.parse(localStorage.getItem(DAILY_STEPS_KEY) || '{}');
      dailySteps[todayKey] = previousDailySteps + sessionSteps;
      localStorage.setItem(DAILY_STEPS_KEY, JSON.stringify(dailySteps));
    }
  }, [sessionSteps, previousDailySteps]);

  // Start GPS tracking on mount only (not on every state change)
  useEffect(() => {
    let mounted = true;
    
    const startGpsTracking = async () => {
      // Check if Geolocation is supported
      if (!('geolocation' in navigator)) {
        setGpsError('GPS not supported on this device');
        toast.error('GPS not available. Step counting disabled.');
        return;
      }
      
      try {
        // Request high accuracy GPS with more frequent updates
        const options = {
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 0  // Always get fresh position (was 1000ms)
        };
        
        const strideLengthMeters = strideLength / 100; // Convert cm to m
        
        // Handle position updates - use refs to avoid stale closures
        const handlePosition = (position) => {
          if (!mounted) {
            console.log('[GPS] Component unmounted, ignoring position');
            return;
          }
          
          const currentIsPaused = isPausedRef.current;
          const currentPhase = phaseRef.current;
          
          console.log('[GPS] handlePosition called:', {
            isPaused: currentIsPaused,
            phase: currentPhase,
            hasLastPosition: !!lastPositionRef.current
          });
          
          // Check refs instead of state to get current values
          if (currentIsPaused) {
            console.log('[GPS] Paused - updating position only');
            // Still update position for when we resume
            lastPositionRef.current = { 
              latitude: position.coords.latitude, 
              longitude: position.coords.longitude 
            };
            return;
          }
          
          if (currentPhase === PHASE.COMPLETE) {
            console.log('[GPS] Workout complete - ignoring position');
            return;
          }
          
          const { latitude, longitude, accuracy } = position.coords;
          setGpsAccuracy(Math.round(accuracy));
          setGpsError(null); // Clear any previous errors
          
          // Accept GPS readings with accuracy up to 100 meters (increased from 50 for better coverage)
          // This may introduce some noise but will capture more distance
          if (accuracy > 100) {
            console.log('[GPS] Accuracy too low:', accuracy, 'meters - waiting for better signal');
            // Still save the position but don't count distance
            lastPositionRef.current = { latitude, longitude };
            return;
          }
          
          if (lastPositionRef.current) {
            const distance = calculateDistance(
              lastPositionRef.current.latitude,
              lastPositionRef.current.longitude,
              latitude,
              longitude
            );
            
            console.log('[GPS] Distance calculated:', distance.toFixed(2), 'meters');
            
            // Filter out GPS noise - require at least 0.5 meter of movement (reduced from 1m for better sensitivity)
            // Cap single reading at 150m to filter extreme GPS jumps (increased from 100m)
            if (distance >= 0.5 && distance < 150) {
              setTotalDistance(prev => {
                const newTotal = prev + distance;
                console.log('[GPS] Distance updated:', prev.toFixed(2), '->', newTotal.toFixed(2));
                return newTotal;
              });
              
              // Calculate steps from distance using stride length
              const newSteps = Math.round(distance / strideLengthMeters);
              if (newSteps > 0) {
                setSessionSteps(prev => {
                  const newTotal = prev + newSteps;
                  console.log('[GPS] Steps updated:', prev, '->', newTotal, '(+', newSteps, ')');
                  return newTotal;
                });
              }
            } else if (distance >= 150) {
              console.log('[GPS] Filtered large jump:', distance.toFixed(1), 'm');
            } else {
              console.log('[GPS] Movement too small:', distance.toFixed(2), 'm (need >= 0.5m)');
            }
          } else {
            console.log('[GPS] First position acquired, accuracy:', accuracy, 'm');
          }
          
          lastPositionRef.current = { latitude, longitude };
        };
        
        const handleError = (error) => {
          if (!mounted) return;
          console.error('[GPS] Error:', error.code, error.message);
          
          switch(error.code) {
            case error.PERMISSION_DENIED:
              setGpsError('GPS permission denied. Please enable location services.');
              toast.error('Please enable GPS for step tracking.');
              break;
            case error.POSITION_UNAVAILABLE:
              setGpsError('GPS signal unavailable. Try moving outdoors.');
              toast.error('Cannot get GPS signal. Try moving outdoors.');
              break;
            case error.TIMEOUT:
              setGpsError('GPS request timed out. Retrying...');
              // Don't show toast for timeout, it will retry automatically
              break;
            default:
              setGpsError('Unknown GPS error');
          }
        };
        
        console.log('[GPS] Starting watch position...');
        
        // Start watching position
        watchIdRef.current = navigator.geolocation.watchPosition(
          (position) => {
            console.log('[GPS] Position received:', {
              lat: position.coords.latitude.toFixed(6),
              lon: position.coords.longitude.toFixed(6),
              accuracy: position.coords.accuracy
            });
            setGpsEnabled(true); // Mark GPS as active on first position
            handlePosition(position);
          },
          handleError,
          options
        );
        
        console.log('[GPS] Watch ID:', watchIdRef.current);
        if (watchIdRef.current !== null && watchIdRef.current !== undefined) {
          setGpsEnabled(true);
          toast.success('📍 GPS tracking activated!');
        }
        
      } catch (error) {
        console.error('[GPS] Error starting tracking:', error);
        setGpsError('Failed to start GPS tracking');
      }
    };
    
    startGpsTracking();
    
    // Cleanup on unmount only
    return () => {
      mounted = false;
      if (watchIdRef.current !== null) {
        console.log('[GPS] Cleaning up watch:', watchIdRef.current);
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
    };
  }, [strideLength]); // Only re-run if stride length changes

  // Play beep sound
  const playBeep = useCallback((frequency = 800, duration = 100) => {
    if (!audioContextRef.current) return;
    
    try {
      const oscillator = audioContextRef.current.createOscillator();
      const gainNode = audioContextRef.current.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContextRef.current.destination);
      
      oscillator.frequency.value = frequency;
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.3, audioContextRef.current.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContextRef.current.currentTime + duration / 1000);
      
      oscillator.start(audioContextRef.current.currentTime);
      oscillator.stop(audioContextRef.current.currentTime + duration / 1000);
    } catch (error) {
      console.error('Audio playback error:', error);
    }
  }, []);

  // Different beep sounds
  const playQuickWalkingBeep = useCallback(() => {
    playBeep(1200, 120);
  }, [playBeep]);

  const playSlowWalkingBeep = useCallback(() => {
    playBeep(600, 150);
  }, [playBeep]);

  const playPhaseChangeBeep = useCallback(() => {
    playBeep(1000, 200);
    setTimeout(() => playBeep(1200, 200), 250);
  }, [playBeep]);

  // Get female English voice from cache or fresh
  const getFemaleEnglishVoice = useCallback(() => {
    // Use cached voices or try to get them again
    let voices = voicesCacheRef.current;
    if (!voices || voices.length === 0) {
      voices = window.speechSynthesis.getVoices();
      if (voices.length > 0) {
        voicesCacheRef.current = voices;
      }
    }
    
    if (!voices || voices.length === 0) return { voice: null, isFemale: false };
    
    // Common female voice names across browsers/OS
    const femaleVoiceNames = [
      'female',
      'samantha',      // macOS/iOS
      'victoria',      // macOS
      'karen',         // macOS Australian
      'moira',         // macOS Irish
      'fiona',         // macOS Scottish
      'tessa',         // macOS South African
      'veena',         // macOS Indian
      'alice',
      'ellen',
      'nicky',         // macOS
      'susan',         // Windows
      'hazel',         // Windows UK
      'heera',         // Windows Indian
      'google uk english female',
      'google us english female',
      'microsoft zira',
      'microsoft eva',
      'microsoft aria',
      'microsoft jenny'
    ];
    
    // Male voice names to explicitly avoid
    const maleVoiceNames = [
      'male', 'david', 'james', 'daniel', 'george', 'richard',
      'mark', 'alex', 'tom', 'fred', 'microsoft mark', 'microsoft david'
    ];
    
    // Prefer female English voices
    let selectedVoice = voices.find(v => 
      v.lang.startsWith('en') && 
      femaleVoiceNames.some(name => v.name.toLowerCase().includes(name))
    );
    
    let isFemale = !!selectedVoice;
    
    if (selectedVoice) {
      console.log('[Voice] Using female voice:', selectedVoice.name);
      return { voice: selectedVoice, isFemale: true };
    }
    
    // If no female voice found, try any English voice that's not explicitly male
    selectedVoice = voices.find(v => 
      v.lang.startsWith('en') && 
      !maleVoiceNames.some(name => v.name.toLowerCase().includes(name))
    );
    
    if (selectedVoice) {
      console.log('[Voice] Using non-male voice:', selectedVoice.name);
      return { voice: selectedVoice, isFemale: false };
    }
    
    // Fallback to any English voice
    selectedVoice = voices.find(v => v.lang.startsWith('en'));
    console.log('[Voice] Using fallback:', selectedVoice?.name || 'default');
    return { voice: selectedVoice || null, isFemale: false };
  }, []);

  // Voice countdown for 5 seconds - English female voice
  const speakNumber = useCallback((number) => {
    try {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(number.toString());
      utterance.lang = 'en-US';
      utterance.volume = 1.0;
      const { voice, isFemale } = getFemaleEnglishVoice();
      if (voice) {
        utterance.voice = voice;
        // Adjust pitch if not a female voice
        if (!isFemale) {
          utterance.pitch = 1.3;
          utterance.rate = 0.95;
        } else {
          utterance.pitch = 1.2;
          utterance.rate = 1.0;
        }
      } else {
        utterance.pitch = 1.3;
        utterance.rate = 0.95;
      }
      window.speechSynthesis.speak(utterance);
    } catch (error) {
      console.error('Speech synthesis error:', error);
    }
  }, [getFemaleEnglishVoice]);

  // Voice announcement for phase changes - English female voice
  const speakPhase = useCallback((text) => {
    try {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'en-US';
      utterance.volume = 1.0;
      const { voice, isFemale } = getFemaleEnglishVoice();
      if (voice) {
        utterance.voice = voice;
        // Adjust pitch if not a female voice
        if (!isFemale) {
          utterance.pitch = 1.3;
          utterance.rate = 0.95;
        } else {
          utterance.pitch = 1.0;
          utterance.rate = 1.0;
        }
      } else {
        utterance.pitch = 1.3;
        utterance.rate = 0.95;
      }
      window.speechSynthesis.speak(utterance);
    } catch (error) {
      console.error('Speech synthesis error:', error);
    }
  }, [getFemaleEnglishVoice]);

  // Handle workout completion
  const completeWorkout = useCallback(() => {
    setPhase(PHASE.COMPLETE);
    toast.success('Walking workout complete! 🎉');
    playBeep(1500, 300);
  }, [playBeep]);

  // Handle phase transitions
  const handlePhaseComplete = useCallback(() => {
    playPhaseChangeBeep();
    
    if (phase === PHASE.QUICK) {
      if (currentSlowSet < config.slowWalkingSets) {
        setPhase(PHASE.SLOW);
        setCurrentSlowSet(prev => prev + 1);
        setTimeLeft(config.slowWalkingTime);
        speakPhase('Slow Walk');
        toast.info('Slow walking - Recovery pace');
      } else {
        completeWorkout();
      }
    } else if (phase === PHASE.SLOW) {
      if (currentQuickSet < config.quickWalkingSets) {
        setPhase(PHASE.QUICK);
        setCurrentQuickSet(prev => prev + 1);
        setTimeLeft(config.quickWalkingTime);
        speakPhase('Quick Walk');
        toast.success('Quick walking - Pick up the pace!');
      } else {
        completeWorkout();
      }
    }
  }, [phase, currentQuickSet, currentSlowSet, config, playPhaseChangeBeep, completeWorkout, speakPhase]);

  // Announce initial phase when component mounts
  useEffect(() => {
    // Small delay to ensure speech synthesis is ready
    const timeout = setTimeout(() => {
      if (phase === PHASE.QUICK && currentQuickSet === 1 && totalElapsedTime === 0) {
        speakPhase('Quick Walk');
      }
    }, 500);
    return () => clearTimeout(timeout);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Main timer effect
  useEffect(() => {
    if (isPaused || phase === PHASE.COMPLETE) return;

    intervalRef.current = setInterval(() => {
      setTotalElapsedTime(prev => prev + 1);
      
      // Track time by phase for calorie calculation
      if (phase === PHASE.QUICK) {
        setQuickWalkingTime(prev => prev + 1);
      } else if (phase === PHASE.SLOW) {
        setSlowWalkingTime(prev => prev + 1);
      }
      
      setTimeLeft(prev => {
        if (prev <= 1) {
          handlePhaseComplete();
          return 0;
        }
        
        if (prev <= 5) {
          speakNumber(prev);
          if (phase === PHASE.QUICK) {
            playQuickWalkingBeep();
          } else {
            playSlowWalkingBeep();
          }
        }
        
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(intervalRef.current);
  }, [isPaused, phase, handlePhaseComplete, speakNumber, playQuickWalkingBeep, playSlowWalkingBeep]);

  // Skip current phase
  const handleSkip = () => {
    clearInterval(intervalRef.current);
    setTimeLeft(0);
    handlePhaseComplete();
  };

  // Format time display
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Get phase-specific styling
  const getPhaseInfo = () => {
    switch (phase) {
      case PHASE.QUICK:
        return {
          title: '🚶‍♂️ Quick Walking',
          subtitle: `Set ${currentQuickSet} of ${config.quickWalkingSets}`,
          color: 'text-cyan-400',
          bgColor: 'bg-cyan-500/10',
          borderColor: 'border-cyan-500',
          progressColor: 'bg-cyan-500',
          maxTime: config.quickWalkingTime
        };
      case PHASE.SLOW:
        return {
          title: '🚶 Slow Walking',
          subtitle: `Set ${currentSlowSet} of ${config.slowWalkingSets}`,
          color: 'text-green-400',
          bgColor: 'bg-green-500/10',
          borderColor: 'border-green-500',
          progressColor: 'bg-green-500',
          maxTime: config.slowWalkingTime
        };
      default:
        return {
          title: 'Complete!',
          subtitle: '',
          color: 'text-yellow-400',
          bgColor: 'bg-yellow-500/10',
          borderColor: 'border-yellow-500',
          progressColor: 'bg-yellow-500',
          maxTime: 1
        };
    }
  };

  const phaseInfo = getPhaseInfo();
  const progress = ((phaseInfo.maxTime - timeLeft) / phaseInfo.maxTime) * 100;

  // Calculate step goal countdown
  const stepsRemaining = Math.max(0, stepGoal - totalDailySteps);
  const stepsExceeded = totalDailySteps > stepGoal ? totalDailySteps - stepGoal : 0;
  const goalProgressPercent = Math.min(100, (totalDailySteps / stepGoal) * 100);

  // Completion Screen
  if (phase === PHASE.COMPLETE) {
    return (
      <div className="max-w-md mx-auto">
        <Card className="bg-slate-900/80 border-2 border-amber-500/50 rounded-2xl shadow-xl shadow-amber-500/10">
          <CardContent className="p-8 space-y-6 text-center">
            {/* Celebration Emoji */}
            <div className="text-8xl animate-bounce">
              🏆
            </div>
            
            {/* Congratulations Message */}
            <div className="space-y-2">
              <h2 className="text-4xl font-heading font-bold text-amber-400">
                You are the best!
              </h2>
              <p className="text-lg text-slate-400">
                Walking Complete! 🎉
              </p>
            </div>
            
            {/* Total Time */}
            <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700/50">
              <div className="text-sm text-slate-400 mb-1">Total Time</div>
              <div className="text-4xl font-mono font-heading font-bold text-cyan-400">{formatTime(totalElapsedTime)}</div>
            </div>
            
            {/* GPS Status & Distance */}
            <div className={`p-4 rounded-xl border ${gpsEnabled ? 'bg-green-500/10 border-green-500/30' : 'bg-slate-800/50 border-slate-700'}`}>
              <div className="flex items-center justify-center gap-2 text-sm mb-1">
                <MapPin className={`h-4 w-4 ${gpsEnabled ? 'text-green-400' : 'text-slate-400'}`} />
                <span className={gpsEnabled ? 'text-green-300' : 'text-slate-400'}>
                  {gpsEnabled ? 'GPS Active' : 'GPS Connecting...'}
                </span>
                {gpsAccuracy && gpsEnabled && (
                  <span className="text-xs text-slate-500">±{gpsAccuracy}m</span>
                )}
              </div>
              <div className="text-3xl font-heading font-bold text-green-400">
                {(totalDistance / 1000).toFixed(2)} <span className="text-lg">km</span>
              </div>
              {gpsError && (
                <div className="text-xs text-red-400 mt-1">{gpsError}</div>
              )}
              
              {/* Screen Wake Lock Toggle */}
              <button
                onClick={toggleWakeLock}
                className={`mt-3 flex items-center justify-center gap-1.5 w-full py-2 rounded-xl text-xs font-medium transition-all ${
                  wakeLockEnabled 
                    ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30' 
                    : 'bg-slate-800 text-slate-400 border border-slate-700'
                }`}
                title={wakeLockEnabled ? 'Screen stays on - tap to disable' : 'Tap to keep screen on'}
              >
                <Smartphone className="h-3.5 w-3.5" />
                {wakeLockEnabled ? '🔆 Screen On' : '📱 Keep Screen On'}
              </button>
              {!wakeLockEnabled && gpsEnabled && (
                <p className="text-[10px] text-amber-400 mt-1 text-center">
                  ⚠️ Enable to track while walking
                </p>
              )}
            </div>
            
            {/* Session Steps */}
            <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl">
              <div className="flex items-center justify-center gap-2 text-sm text-blue-300 mb-1">
                <Footprints className="h-4 w-4" />
                Session Steps (GPS)
              </div>
              <div className="text-4xl font-heading font-bold text-blue-400">{sessionSteps.toLocaleString()}</div>
              <div className="text-xs text-slate-500 mt-1">
                Stride: {strideLength}cm
              </div>
            </div>
            
            {/* Step Goal Progress - Only if enabled */}
            {stepGoalEnabled && (
              <div className={`p-4 rounded-xl border ${
                stepsExceeded > 0 
                  ? 'bg-green-500/10 border-green-500/30' 
                  : 'bg-blue-500/10 border-blue-500/30'
              }`}>
                <div className="flex items-center justify-center gap-2 text-sm mb-2">
                  <Target className="h-4 w-4" />
                  <span className={stepsExceeded > 0 ? 'text-green-300' : 'text-purple-300'}>
                    Daily Step Goal
                  </span>
                </div>
                
                {/* Progress Bar */}
                <div className="w-full bg-slate-700 rounded-full h-3 overflow-hidden mb-3">
                  <div 
                    className={`h-full transition-all duration-500 ${
                      goalProgressPercent >= 100 ? 'bg-green-500' : 'bg-purple-500'
                    }`}
                    style={{ width: `${goalProgressPercent}%` }}
                  />
                </div>
                
                {/* Countdown or Exceeded */}
                {stepsExceeded > 0 ? (
                  <div className="text-green-400 font-bold text-2xl">
                    🎉 +{stepsExceeded.toLocaleString()} steps!
                  </div>
                ) : (
                  <div>
                    <div className="text-3xl font-bold text-purple-400">
                      {stepsRemaining.toLocaleString()}
                    </div>
                    <div className="text-sm text-purple-300">steps to goal</div>
                  </div>
                )}
                
                {/* Steps breakdown */}
                <div className="text-xs text-muted-foreground mt-2 space-y-1">
                  <div className="flex justify-between">
                    <span>Total Today:</span>
                    <span className="font-medium text-white">{totalDailySteps.toLocaleString()} / {stepGoal.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-slate-500">
                    <span className="flex items-center gap-1">
                      <Footprints className="h-3 w-3" /> Walking Tracker:
                    </span>
                    <span>{walkingTrackerSteps.toLocaleString()}</span>
                  </div>
                  {externalSteps > 0 && (
                    <div className="flex justify-between text-cyan-400">
                      <span className="flex items-center gap-1">
                        <Watch className="h-3 w-3" /> External/Smartwatch:
                      </span>
                      <span>{externalSteps.toLocaleString()}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Calories Burned - With Correction Option */}
            <div className="p-4 bg-orange-500/20 border border-orange-500/30 rounded-lg">
              <div className="flex items-center justify-center gap-2 text-sm text-orange-300 mb-2">
                <Flame className="h-4 w-4" />
                Calories Burned
              </div>
              
              {editingCalories ? (
                <div className="flex items-center justify-center gap-2">
                  <Input
                    type="number"
                    value={manualCalories !== null ? manualCalories : calculatedCalories}
                    onChange={(e) => setManualCalories(Math.max(0, parseInt(e.target.value) || 0))}
                    className="w-24 text-center text-xl font-bold"
                    min="0"
                    max="9999"
                    data-testid="calories-input"
                  />
                  <span className="text-orange-400">kcal</span>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setEditingCalories(false)}
                    className="text-green-400 hover:text-green-300"
                  >
                    <Check className="h-5 w-5" />
                  </Button>
                </div>
              ) : (
                <div className="flex items-center justify-center gap-2">
                  <div className="text-4xl font-bold text-orange-400">
                    {displayCalories}
                  </div>
                  <span className="text-orange-300 text-lg">kcal</span>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setEditingCalories(true)}
                    className="text-orange-400 hover:text-orange-300 ml-2"
                    data-testid="edit-calories-btn"
                  >
                    <Edit3 className="h-4 w-4" />
                  </Button>
                </div>
              )}
              
              {manualCalories !== null && manualCalories !== calculatedCalories && (
                <div className="text-xs text-muted-foreground mt-2">
                  Estimated: {calculatedCalories} kcal (adjusted)
                </div>
              )}
              
              <div className="text-xs text-muted-foreground mt-1">
                Based on {bodyWeight}kg × MET formula
              </div>
            </div>
            
            {/* Summary */}
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="p-3 bg-cyan-500/20 rounded-lg">
                <div className="text-cyan-300">Quick Walking</div>
                <div className="text-xl font-bold">{config.quickWalkingSets} sets</div>
              </div>
              <div className="p-3 bg-green-500/20 rounded-lg">
                <div className="text-green-300">Slow Walking</div>
                <div className="text-xl font-bold">{config.slowWalkingSets} sets</div>
              </div>
            </div>

            {/* Total Intervals */}
            <div className="p-4 bg-muted/30 rounded-lg">
              <div className="text-2xl font-bold text-primary">
                {config.quickWalkingSets + config.slowWalkingSets}
              </div>
              <div className="text-sm text-muted-foreground">Total Walking Cycles</div>
              <div className="text-xs text-muted-foreground mt-1">
                (Quick + Slow intervals)
              </div>
            </div>
            
            {/* Done Button */}
            <Button
              onClick={() => handleWorkoutComplete({
                totalTime: totalElapsedTime,
                quickSets: config.quickWalkingSets,
                slowSets: config.slowWalkingSets,
                steps: sessionSteps,
                totalDailySteps: totalDailySteps,
                calories: displayCalories,
                bodyWeight: bodyWeight
              })}
              className="w-full h-12 text-base font-semibold bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600"
              size="lg"
              data-testid="walking-done-btn"
            >
              Done - Back to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-slate-950 border-none ios-header" style={{ border: 'none' }}>
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleSaveAndExit}
              className="rounded-full text-white hover:bg-slate-800"
              data-testid="walking-back-btn"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center shadow-lg shadow-blue-500/20">
                <Footprints className="h-5 w-5 text-white" />
              </div>
              <h1 className="text-lg font-heading font-bold text-white">Walking Timer</h1>
            </div>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setShowOnboarding(true)} 
            className="text-slate-400 hover:text-blue-400"
            title="Help"
          >
            <HelpCircle className="h-4 w-4" />
          </Button>
        </div>
      </header>
      
      <main className="container mx-auto px-4 py-6 max-w-md space-y-4">
        {/* Main Timer Card */}
        <Card className={`bg-slate-900/80 border-2 ${phaseInfo.borderColor} rounded-2xl`}>
          <CardContent className="p-6 space-y-6">
            {/* Total Time, Steps & Distance Row */}
            <div className="grid grid-cols-3 gap-3">
              <div className="text-center p-3 bg-slate-800/50 rounded-xl">
                <div className="text-xs text-slate-400 mb-1 font-medium">Total Time</div>
                <div className="text-xl font-mono font-heading font-bold text-amber-400">{formatTime(totalElapsedTime)}</div>
              </div>
              <div className="text-center p-3 bg-slate-800/50 rounded-xl">
                <div className="flex items-center justify-center gap-1 text-xs text-slate-400 mb-1">
                  <MapPin className="h-3 w-3" />
                  <span className="font-medium">Distance</span>
                </div>
                <div className="text-xl font-heading font-bold text-green-400">
                  {(totalDistance / 1000).toFixed(2)}km
                </div>
              </div>
              <div className="text-center p-3 bg-slate-800/50 rounded-xl">
                <div className="flex items-center justify-center gap-1 text-xs text-slate-400 mb-1">
                  <Footprints className="h-3 w-3" />
                  <span className="font-medium">Steps</span>
                </div>
                <div className="text-xl font-heading font-bold text-blue-400">
                  {sessionSteps.toLocaleString()}
                </div>
                {gpsError && (
                  <div className="text-xs text-red-400 mt-1">{gpsError}</div>
                )}
              </div>
            </div>

            {/* Step Goal Countdown - Only if enabled */}
            {stepGoalEnabled && (
              <div className={`p-4 rounded-xl ${
                stepsExceeded > 0 
                  ? 'bg-green-500/20 border border-green-500/30' 
                  : 'bg-blue-500/20 border border-blue-500/30'
              }`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-1 text-xs">
                    <Target className="h-3 w-3" />
                    <span className={stepsExceeded > 0 ? 'text-green-300' : 'text-blue-300'}>
                      Goal: {stepGoal.toLocaleString()}
                    </span>
                  </div>
                  <span className="text-xs text-slate-400">
                    Today: {totalDailySteps.toLocaleString()}
                  </span>
                </div>
              
                {/* Progress Bar */}
                <div className="w-full bg-slate-800 rounded-full h-2.5 overflow-hidden mb-2">
                  <div 
                    className={`h-full transition-all duration-500 rounded-full ${
                      goalProgressPercent >= 100 ? 'bg-gradient-to-r from-green-500 to-emerald-500' : 'bg-gradient-to-r from-blue-500 to-cyan-500'
                    }`}
                    style={{ width: `${goalProgressPercent}%` }}
                  />
                </div>
              
                {/* Countdown Display */}
                <div className="text-center">
                  {stepsExceeded > 0 ? (
                  <span className="text-green-400 font-bold text-lg">
                    🎉 +{stepsExceeded.toLocaleString()} steps!
                  </span>
                ) : (
                  <span className="text-purple-300">
                    <span className="font-bold text-xl text-purple-400">{stepsRemaining.toLocaleString()}</span>
                    <span className="text-xs ml-1">steps to go</span>
                  </span>
                )}
              </div>
              
              {/* Steps source breakdown */}
              {externalSteps > 0 && (
                <div className="text-[10px] text-center mt-1 text-slate-500">
                  <span className="text-slate-400">{walkingTrackerSteps.toLocaleString()}</span> tracker + 
                  <span className="text-cyan-400 ml-1">{externalSteps.toLocaleString()}</span> external
                </div>
              )}
            </div>
          )}

          {/* Phase Title */}
          <div className="text-center space-y-2">
            <h2 className={`text-2xl font-bold tracking-wide ${phaseInfo.color}`}>
              {phaseInfo.title}
            </h2>
            <p className="text-sm text-muted-foreground">{phaseInfo.subtitle}</p>
          </div>

          {/* Main Timer */}
          <div className="text-center py-8">
            <div className={`text-8xl font-mono font-bold ${phaseInfo.color} tracking-tight`}>
              {formatTime(timeLeft)}
            </div>
          </div>

          {/* Progress Bar */}
          <div className="w-full bg-muted rounded-full h-4 overflow-hidden">
            <div 
              className={`h-full transition-all duration-500 ${phaseInfo.progressColor}`}
              style={{ width: `${progress}%` }}
            />
          </div>

          {/* Control Buttons */}
          <div className="flex gap-3 pt-4">
            <Button
              onClick={() => setIsPaused(!isPaused)}
              variant="outline"
              size="lg"
              className="flex-1 h-14 text-base font-semibold"
              data-testid="walking-pause-resume-btn"
            >
              {isPaused ? (
                <>
                  <Play className="mr-2 h-5 w-5" />
                  Resume
                </>
              ) : (
                <>
                  <Pause className="mr-2 h-5 w-5" />
                  Pause
                </>
              )}
            </Button>
            <Button
              onClick={handleSkip}
              variant="outline"
              size="lg"
              className="flex-1 h-14 text-base font-semibold"
              data-testid="walking-skip-btn"
            >
              <SkipForward className="mr-2 h-5 w-5" />
              Skip
            </Button>
          </div>

          {/* Save & Exit Button */}
          <Button
            onClick={handleSaveAndExit}
            variant="outline"
            className="w-full border-green-500 text-green-400 hover:bg-green-500/20"
            data-testid="walking-exit-btn"
          >
            <Check className="mr-2 h-4 w-4" />
            Save & Exit Workout
          </Button>

          {/* Workout Progress */}
          <div className="grid grid-cols-2 gap-4 pt-4 border-t border-border">
            <div className={`text-center p-3 rounded-lg ${phase === PHASE.QUICK ? 'bg-cyan-500/20' : 'bg-muted/30'}`}>
              <div className="text-xs text-muted-foreground mb-1">Quick Walking</div>
              <div className={`font-bold text-lg ${phase === PHASE.QUICK ? 'text-cyan-400' : ''}`}>
                {currentQuickSet}/{config.quickWalkingSets}
              </div>
            </div>
            <div className={`text-center p-3 rounded-lg ${phase === PHASE.SLOW ? 'bg-green-500/20' : 'bg-muted/30'}`}>
              <div className="text-xs text-muted-foreground mb-1">Slow Walking</div>
              <div className={`font-bold text-lg ${phase === PHASE.SLOW ? 'text-green-400' : ''}`}>
                {currentSlowSet}/{config.slowWalkingSets}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      </main>
    </div>
  );
}
